import { useState, useEffect, useCallback } from 'react';
import { User, BirdieGameGroup, Course } from '@/types';
import { getGolferGameIds } from '@/utils/scoringUtils';
import { fetchHoleTileData, betterScoreType } from '@/integrations/supabase/scores';
import type { HoleTileData } from '@/integrations/supabase/scores';

interface UseHoleScoringParams {
  golfers: User[];
  course: Course | null;
  eligibleGames: BirdieGameGroup[];
  gameId?: string;
  mode: 'game' | 'scoring';
}

export function useHoleScoring({ golfers, course, eligibleGames, gameId, mode }: UseHoleScoringParams) {
  const [activeGolferId, setActiveGolferId] = useState<string | 'all'>('all');
  const [selectedHole, setSelectedHole] = useState<number | null>(null);
  const [selectedGolfer, setSelectedGolfer] = useState<User | null>(null);
  const [isRecordModalOpen, setIsRecordModalOpen] = useState(false);
  const [isGolferSelectOpen, setIsGolferSelectOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [holeTileData, setHoleTileData] = useState<Map<number, HoleTileData>>(new Map());
  const [lastFetchedGameId, setLastFetchedGameId] = useState<string | undefined>();

  // Game mode: fetch tile data from DB
  useEffect(() => {
    if (mode !== 'game' || !course) return;

    const resolvedGameId = gameId || (eligibleGames.length > 0 ? eligibleGames[0].id : undefined);
    if (!resolvedGameId) return;

    let cancelled = false;
    const load = async () => {
      const data = await fetchHoleTileData({
        courseId: course.id,
        gameId: resolvedGameId,
      });
      if (!cancelled) {
        setHoleTileData(data);
        setLastFetchedGameId(resolvedGameId);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [mode, course, gameId, eligibleGames, refreshKey]);

  // Scoring mode: no fetch. Tile data starts empty and is updated locally.

  const handleHoleClick = useCallback((holeNumber: number) => {
    setSelectedHole(holeNumber);

    if (golfers.length === 1) {
      setSelectedGolfer(golfers[0]);
      setIsRecordModalOpen(true);
    } else if (activeGolferId !== 'all') {
      const golfer = golfers.find(g => g.id === activeGolferId);
      if (golfer) {
        setSelectedGolfer(golfer);
        setIsRecordModalOpen(true);
      }
    } else {
      // Multiple golfers with "All" tab active — show golfer selection
      setIsGolferSelectOpen(true);
    }
  }, [golfers, activeGolferId]);

  const handleGolferSelect = useCallback((golfer: User) => {
    setSelectedGolfer(golfer);
    setIsGolferSelectOpen(false);
    setIsRecordModalOpen(true);
  }, []);

  const handleBirdieRecorded = useCallback(() => {
    // In scoring mode, update tile data locally (session-scoped)
    if (mode === 'scoring' && selectedHole !== null && selectedGolfer) {
      setHoleTileData(prev => {
        const next = new Map(prev);
        const existing = next.get(selectedHole);
        const tile: HoleTileData = existing
          ? { ...existing, golferBirdies: [...existing.golferBirdies], teeBoxBirdies: [...existing.teeBoxBirdies] }
          : { holeNumber: selectedHole, golferBirdies: [], bestScoreType: undefined, teeBoxBirdies: [] };

        // Add golfer if not already present on this hole
        if (!tile.golferBirdies.some(g => g.userId === selectedGolfer.id)) {
          tile.golferBirdies.push({
            userId: selectedGolfer.id,
            name: selectedGolfer.name,
            imageUrl: selectedGolfer.imageUrl || null,
            bestScoreType: 'birdie',
          });
        }

        tile.bestScoreType = betterScoreType(tile.bestScoreType, 'birdie');
        next.set(selectedHole, tile);
        return next;
      });
    }

    // In game mode, refreshKey triggers a re-fetch
    setRefreshKey(prev => prev + 1);
  }, [mode, selectedHole, selectedGolfer]);

  const closeModal = useCallback(() => {
    setIsRecordModalOpen(false);
    setIsGolferSelectOpen(false);
    setSelectedHole(null);
    setSelectedGolfer(null);
  }, []);

  const filteredGolfers = activeGolferId === 'all'
    ? golfers
    : golfers.filter(g => g.id === activeGolferId);

  const currentGameId = gameId || (eligibleGames.length > 0 ? eligibleGames[0].id : undefined);

  const currentGolferGameIds = selectedGolfer
    ? getGolferGameIds(selectedGolfer.id, eligibleGames)
    : [];

  return {
    // State
    activeGolferId,
    setActiveGolferId,
    selectedHole,
    selectedGolfer,
    isRecordModalOpen,
    isGolferSelectOpen,
    refreshKey,
    holeTileData,
    lastFetchedGameId,
    // Derived
    filteredGolfers,
    currentGameId,
    currentGolferGameIds,
    // Actions
    handleHoleClick,
    handleGolferSelect,
    handleBirdieRecorded,
    closeModal,
  };
}
